import React from 'react';
import { useEcoLayoutTuning } from '../../contexts/EcoLayoutTuningContext';
import { applySlotTuning } from '../../lib/rhythm/ecoLayoutTuning';
import {
  isMeasureComplete,
  slotsPerMeasure,
  wagonRef,
  type EcoRitmoSessionConfig,
  type WagonRef,
} from '../../lib/rhythm/ecoRitmoConfig';
import type { RhythmFigureId } from '../../lib/rhythm/rhythmTokens';
import type { TrainSpriteDef } from '../../lib/rhythm/trainSprites';
import { EcoWagonSlot } from './EcoWagonSlot';
import type { FigureCellState } from './FigureGrid';

type EcoTrainSpriteMeasureBase = {
  measureIndex: number;
  config: EcoRitmoSessionConfig;
  sprite: TrainSpriteDef;
};

type EcoTrainSpriteMeasureBuilder = EcoTrainSpriteMeasureBase & {
  mode?: 'builder';
  selectedWagon: WagonRef;
  onSelectWagon: (ref: WagonRef) => void;
  onSetFigure: (ref: WagonRef, fig: RhythmFigureId | null) => void;
};

type EcoTrainSpriteMeasurePlay = EcoTrainSpriteMeasureBase & {
  mode: 'play';
  isSlotActive: (ref: WagonRef) => boolean;
  slotPlayState: (measureIndex: number, slotIndex: number) => FigureCellState;
};

export type EcoTrainSpriteMeasureProps =
  | EcoTrainSpriteMeasureBuilder
  | EcoTrainSpriteMeasurePlay;

export const EcoTrainSpriteMeasure: React.FC<EcoTrainSpriteMeasureProps> = (props) => {
  const { measureIndex, config, sprite } = props;
  const slots = config.pattern[measureIndex] ?? [];
  const nSlots = slotsPerMeasure(config.timeSignature);
  const complete = isMeasureComplete(slots, config.timeSignature);
  const { slotTuningFor } = useEcoLayoutTuning();
  const slotTuning = slotTuningFor(config.timeSignature);
  const isPlay = props.mode === 'play';

  return (
    <div className="eco-measure-sprite flex flex-col items-center h-full min-h-0 min-w-0">
      <span
        className={`eco-measure-label shrink-0 ${complete || isPlay ? 'eco-measure-label--done' : ''}`}
      >
        Compás {measureIndex + 1}
      </span>
      <div className="eco-measure-sprite-train flex-1 min-h-0 w-full">
        <div className="eco-train-sprite-wrap">
          <div
            className="eco-train-sprite-media"
            style={
              {
                aspectRatio: String(sprite.aspectRatio),
                ['--sprite-ar' as string]: String(sprite.aspectRatio),
              } as React.CSSProperties
            }
          >
            <img
              src={sprite.src}
              srcSet={sprite.src2x ? `${sprite.src} 1x, ${sprite.src2x} 2x` : undefined}
              alt=""
              className="eco-train-sprite-img"
              draggable={false}
            />
            <div className="eco-train-sprite-slots">
            {Array.from({ length: nSlots }).map((_, slotIndex) => {
              const ref = wagonRef(measureIndex, slotIndex);
              const layout = sprite.slots[slotIndex]
                ? applySlotTuning(sprite.slots[slotIndex], slotTuning)
                : undefined;

              if (isPlay) {
                return (
                  <EcoWagonSlot
                    key={slotIndex}
                    measureIndex={measureIndex}
                    slotIndex={slotIndex}
                    figure={slots[slotIndex] ?? null}
                    selected={props.isSlotActive(ref)}
                    onSelect={() => {}}
                    onSetFigure={() => {}}
                    layout={layout}
                    variant="sprite"
                    readOnly
                    playState={props.slotPlayState(measureIndex, slotIndex)}
                  />
                );
              }

              return (
                <EcoWagonSlot
                  key={slotIndex}
                  measureIndex={measureIndex}
                  slotIndex={slotIndex}
                  figure={slots[slotIndex] ?? null}
                  selected={
                    props.selectedWagon.measureIndex === measureIndex &&
                    props.selectedWagon.slotIndex === slotIndex
                  }
                  onSelect={() => props.onSelectWagon(ref)}
                  onSetFigure={(fig) => props.onSetFigure(ref, fig)}
                  layout={layout}
                  variant="sprite"
                />
              );
            })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EcoTrainSpriteMeasure;
